import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useMediaQuery, useTheme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import { loadReferralsByOrgId } from './actions/load-referrals';
import { referralsForOrgIdSelector, referralsStateForOrgIdSelector } from './referrals-selectors';
import { userSelector } from '../reducers/current-user-selectors';

import { UiBreadcrumbHeader } from '../ui/ui-breadcrumb-header';

import { AddManualReferralDialog } from './add-manual-referral-dialog';
import { EmployeeReferralTable } from './employee-referral-table';
import { ReactComponent as Step1 } from './illustrations/step-1.svg';
import { ReactComponent as Step2 } from './illustrations/step-2.svg';
import { ReactComponent as Step3 } from './illustrations/step-3.svg';
import { ReferralRequestDialog } from './referral-request-dialog';

import { HeaderButtonAddIcon } from '../util/material-design-icons';
import { EMPLOYEE_PERMISSION, HIRING_PERMISSION } from '../util/permissions';
import { sortByDate } from '../util/sort-functions';
import { LoadingMessage } from '../widgets/loading-message';
import { NotFoundPage } from '../widgets/not-found-page';

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  referrals: referralsForOrgIdSelector(state, orgId),
  referralsState: referralsStateForOrgIdSelector(state, orgId),
  user: userSelector(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadReferrals: () => dispatch(loadReferralsByOrgId(orgId, true)),
  },
});

type InputProps = {
  orgId: number;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

const ReferralStep = ({
  title,
  description,
  Image,
}: {
  title: string;
  description: string;
  Image: any;
}) => (
  <Grid item xs={12} sm={4} className="u-textAlign-center u-margin-y-xl">
    <div className="u-maxWidth-300px u-display-inlineBlock">
      <div className="typo-h6">{title}</div>
      <Image className="u-margin-y u-maxHeight-150px u-maxWidth-100" />
      <div>{description}</div>
    </div>
  </Grid>
);

export const EmployeeReferrals = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { orgId, referrals, user, actions } = props;
  const [isSendingAnnouncement, setIsSendingAnnouncement] = useState<boolean>(false);
  const [isManuallyAddingReferral, setIsManuallyAddingReferral] = useState<boolean>(false);

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const sortedLinkedReferrals = referrals
    .filter((ref) => Boolean(ref.Employee))
    .sort(sortByDate((ref) => ref.datetime));
  const sortedUnlinkedReferrals = referrals
    .filter((ref) => !ref.Employee)
    .sort(sortByDate((ref) => ref.datetime));

  if (
    user.employeesPermission !== EMPLOYEE_PERMISSION.ALL ||
    user.hiringPermission !== HIRING_PERMISSION.ALL
  ) {
    return (
      <NotFoundPage message={"We're sorry, but you don't have permission to access this page."} />
    );
  }

  useEffect(() => {
    actions.loadReferrals();
  });

  const toggleIsSendingAnnouncement = () => {
    setIsSendingAnnouncement(!isSendingAnnouncement);
  };

  const toggleIsManuallyAddingReferral = () => {
    setIsManuallyAddingReferral(!isManuallyAddingReferral);
  };

  if (!referrals) {
    return (
      <div className="card">
        <LoadingMessage />
      </div>
    );
  }

  return (
    <div>
      <UiBreadcrumbHeader
        className="u-position-top-sticky u-background-offWhite"
        pageTitle="Referrals"
        primaryActions={[
          {
            name: 'Ask For Referrals',
            handler: toggleIsSendingAnnouncement,
            style: 'u-height-headerButton',
          },
        ]}
        secondaryAction={{
          name: isOnMobile ? 'Add' : 'Add Referral',
          handler: toggleIsManuallyAddingReferral,
          Icon: <HeaderButtonAddIcon />,
          style: 'u-height-headerButton',
        }}
      />
      <div className="u-margin-lg">
        <Grid
          container
          alignItems="flex-start"
          className="card card-body u-spread u-margin-bottom-lg u-padding-2x"
        >
          <Grid item xs={12} className="typo-h5 u-textAlign-center">
            Here&apos;s How It Works
          </Grid>
          <ReferralStep
            title="1. You Ask For Referrals"
            Image={Step1}
            description="Send a message to your employees asking for referrals. They'll each receive a personalized link they can visit."
          />
          <ReferralStep
            title="2. Employees Give Suggestions"
            Image={Step2}
            description="Once they do, they'll be asked for the names and contact info of people they think would be a good fit."
          />
          <ReferralStep
            title="3. We Reach Out"
            Image={Step3}
            description="Once a person is referred, we'll automatically reach out to them with a link to the job application, prompting them to apply."
          />
        </Grid>
        <div>
          {sortedUnlinkedReferrals.length > 0 && (
            <>
              <div className="card-head">
                <div className="typo-h6">Link Referral to Employee</div>
                <div className="typo-subtitle">
                  These referrals are not currently associated with any employees in the directory.
                  Please specify which employee made each of the below referrals.
                </div>
              </div>
              <EmployeeReferralTable
                referrals={sortedUnlinkedReferrals}
                orgId={orgId}
                isLinked={false}
              />
            </>
          )}
        </div>
        <div>
          <div className="card-head" />
          <EmployeeReferralTable referrals={sortedLinkedReferrals} orgId={orgId} isLinked />
        </div>
        {isSendingAnnouncement && (
          <ReferralRequestDialog
            onClose={toggleIsSendingAnnouncement}
            orgId={orgId}
            initialMessage="Hi {{{employee.casualName}}} - This is {{{user.firstName}}} at {{{employee.companyName}}}. As you probably know, we are hiring and would love any referrals that you might have! You can submit them here: {{{employee.referralLink}}}"
            notification="Great! Your referral message was sent"
          />
        )}
        {isManuallyAddingReferral && (
          <AddManualReferralDialog orgId={orgId} onClose={toggleIsManuallyAddingReferral} />
        )}
      </div>
    </div>
  );
});
